<script>
  import { Meteor } from 'meteor/meteor';
  import { Tracker } from 'meteor/tracker';
  import { onMount } from 'svelte';
  import { ApplicationsCollection } from '../../../api/applications/applications';
  import LeadNotesCell from '../../components/mgmt/LeadNotesCell.svelte';
  import LeadNotesRow from '../../components/mgmt/LeadNotesRow.svelte';
  import LeadStatusActions from '../../components/mgmt/LeadStatusActions.svelte';
  import { sortedNotes } from './leadHelpers';
  import { callMethod } from './managementMethods';

  let { onActionMessage = () => {} } = $props();

  let applications = $state([]);
  let expandedNotesId = $state('');

  onMount(() => {
    const applicationsSub = Meteor.subscribe('mgmt.leads.applications');
    const computation = Tracker.autorun(() => {
      applications = ApplicationsCollection.find({}, { sort: { createdAt: -1 } }).fetch();
    });

    return () => {
      computation.stop();
      applicationsSub.stop();
    };
  });

  function toggleNotes(entryId) {
    expandedNotesId = expandedNotesId === entryId ? '' : entryId;
  }

  async function updateLeadStatus(kind, id, status) {
    const note = prompt('Add note (required for status update):', 'Status updated by management');
    if (!note) return;

    try {
      await callMethod('applications.updateStatus', { applicationId: id, status, note });
    } catch (error) {
      onActionMessage(error.reason || 'Lead status update failed.');
    }
  }

  async function removeClosedApplication(app) {
    const applicantName = [app.firstName, app.middleName, app.lastName].filter(Boolean).join(' ');
    if (!confirm(`Remove ${applicantName} from financing applications? This cannot be undone.`)) return;

    try {
      await callMethod('applications.removeClosed', { applicationId: app._id });
      onActionMessage('Financing application removed.');
      if (expandedNotesId === app._id) expandedNotesId = '';
    } catch (error) {
      onActionMessage(error.reason || 'Could not remove financing application.');
    }
  }
</script>

<div class="management-section">
  <h2>Financing Applications</h2>
  <table class="lead-table">
    <thead>
      <tr><th>Applicant</th><th>Contact</th><th>Location</th><th>Application</th><th>Status</th><th>Notes</th><th>Actions</th></tr>
    </thead>
    <tbody>
      {#each applications as app (app._id)}
        {@const notes = sortedNotes(app)}
        <tr>
          <td>
            <strong>{app.firstName} {app.middleName || ''} {app.lastName}</strong>
            <p class="lead-detail">DOB: {app.dateOfBirth}</p>
            <p class="lead-detail">{app.createdAt?.toLocaleString?.([Meteor.settings.public.locale], Meteor.settings.public.datestamp) || '-'}</p>
          </td>
          <td>
            <a href={`mailto:${app.emailAddress}`}>{app.emailAddress}</a>
            <p class="lead-detail">P: {app.phoneNumber}</p>
            <p class="lead-detail">E: {app.employerPhoneNumber}</p>
          </td>
          <td>
            {app.city}, {app.province}
            <p class="lead-detail">{app.streetAddress} {app.addressLine2 || ''}</p>
            <p class="lead-detail">{app.postalCode}</p>
          </td>
          <td>
            {app.transactionType}
            <p class="lead-detail">{app.incomeType}</p>
            <p class="lead-detail">{app.employer}</p>
          </td>
          <td class="status-cell">
            <span>{app.status}</span>
            {#if app.status === 'closed'}
              <button
                type="button"
                class="danger-btn status-remove-btn"
                aria-label={`Remove ${app.firstName} ${app.lastName}`}
                title="Remove closed application"
                onclick={() => removeClosedApplication(app)}
              >
                X
              </button>
            {/if}
          </td>
          <td>
            <LeadNotesCell
              entryId={app._id}
              {notes}
              {expandedNotesId}
              onToggle={toggleNotes}
            />
          </td>
          <td>
            <LeadStatusActions
              kind="application"
              entryId={app._id}
              onUpdate={updateLeadStatus}
            />
          </td>
        </tr>
        {#if expandedNotesId === app._id}
          <LeadNotesRow {notes} colSpan={7} />
        {/if}
      {:else}
        <tr><td colspan="7">No financing applications yet.</td></tr>
      {/each}
    </tbody>
  </table>
</div>
